import { inject, Injectable } from '@angular/core';
import {
  addDoc,
  collection,
  doc,
  documentId,
  endAt,
  Firestore,
  getDoc,
  getDocs, limit,
  onSnapshot,
  orderBy, Query,
  query,
  startAt,
  Unsubscribe,
  updateDoc,
  where
} from '@angular/fire/firestore';
import { Order } from '../models/order.model';
import { DataService } from './data.service';
import { UploadService } from './upload.service';
import { ProductOrder } from '../models/product-order.model';
import { OrderState } from '../enums/order-state.enum';
import { FileTask } from '../models/file-task.model';
import { FileImportTask } from '../models/file-import-task.model';
import { NestingTask } from '../models/nesting-task.model';
import { BehaviorSubject } from "rxjs";
import { ObjectHelper } from "../helpers/object.helper";
import { Functions, httpsCallable, HttpsCallableResult } from '@angular/fire/functions';
import { FileType } from "../enums/zip-file-type.enum";
import { OrganizationService } from "./organization.service";

@Injectable({
  providedIn: 'root',
})
/**
 * Order service
 */
export class OrderService {

  private firestore = inject(Firestore);
  private uploadService = inject(UploadService);
  private functions = inject(Functions);
  private dataService = inject(DataService);
  private organizationService = inject(OrganizationService);
  private unsubscribeFileTasks: Unsubscribe;
  private fileTasks$: BehaviorSubject<FileTask[]>;
  private unsubscribeImportTasks: Unsubscribe;
  private importTasks$: BehaviorSubject<FileImportTask[]>;
  private unsubscribeNestingTasks: Unsubscribe;
  private nestingTasks$: BehaviorSubject<NestingTask[]>;

  /**
   * Retrieves a query of pre-press orders filtered by the provided states and sorted by date in descending order.
   * @param states - Array of order states to filter the query.
   * @returns A Firestore query object containing orders matching the specified states.
   */
  public getPrePressOrders(states: OrderState[]): Query<Order> {
    return query(collection(this.firestore, 'orders'),
      where('organizationId', '==', this.organizationService.currentOrganization.id),
      where('state', 'in', states),
      orderBy('date', 'desc'))
      .withConverter(Order.converter);
  }

  /**
   * Retrieves a query for customer orders based on the provided customer IDs,
   * filtered by organization ID and specific order states, and sorted by date in descending order.
   * @param customerIds An array of customer IDs to filter the orders.
   * @returns A Firestore query for retrieving orders matching the criteria.
   */
  public getCustomerOrders(customerIds: string[]): Query<Order> {
    return query(collection(this.firestore, 'orders'),
      where('organizationId', '==', this.organizationService.currentOrganization.id),
      where('state', 'in', [
        OrderState.WAIT_FILES, OrderState.PROCESSING, OrderState.WAIT_BAT, OrderState.BAT_ACCEPTED,
        OrderState.BAT_REFUSED, OrderState.PRODUCTION, OrderState.SHIPPED, OrderState.INVOICING]),
      where('customerId', 'in', customerIds),
      orderBy('date', 'desc'))
      .withConverter(Order.converter);
  }

  /**
   * Retrieves and filters orders by a list of order IDs. The method queries the 'orders' collection,
   * filtering orders by the current organization's ID, specific order states, and the provided order IDs.
   * Orders are sorted in descending order based on the 'date' field.
   * @param orderIds - An array of order IDs to filter the query results.
   * @returns A Firestore query object for retrieving the filtered list of orders with
   * a specific converter applied.
   */
  public getFilteredByIdsOrders(orderIds: string[]): Query<Order> {
    return query(collection(this.firestore, 'orders'),
      where('organizationId', '==', this.organizationService.currentOrganization.id),
      where(documentId(), 'in', orderIds),
      orderBy('date', 'desc'))
      .withConverter(Order.converter);
  }

  /**
   * Get orders between two dates
   * @param startDate The start date
   * @param endDate The end date
   * @returns The orders observable
   */
  public getOrdersBetweenDates(startDate: Date, endDate: Date): Query<Order> {
    return query(collection(this.firestore, 'orders'),
      where('organizationId', '==', this.organizationService.currentOrganization.id),
      where('state', 'in', [OrderState.FINISHED]),
      orderBy('date', 'desc'),
      startAt(endDate),
      endAt(startDate))
      .withConverter(Order.converter);
  }

  /**
   * Get orders filtered by customer id
   * @param startDate The start date
   * @param endDate The end date
   * @param customerIds The customer id
   * @returns The query observable
   */
  public ordersByCustomerBetweenDates(startDate: Date, endDate: Date, customerIds: string[]): Query<Order> {
    return query(collection(this.firestore, 'orders'),
      where('organizationId', '==', this.organizationService.currentOrganization.id),
      where('state', 'in', [OrderState.FINISHED]),
      where('customerId', 'in', customerIds),
      orderBy('date', 'desc'),
      startAt(endDate),
      endAt(startDate))
      .withConverter(Order.converter);
  }

  /**
   * Get orders by transactionId
   * @param transactionId The order transaction id
   * @returns The order or null
   */
  public getOrderByTransactionId(transactionId: string): Promise<Order | null> {
    const q = query(collection(this.firestore, 'orders'),
      where('organizationId', '==', this.organizationService.currentOrganization.id),
      where('transactionId', '==', transactionId))
      .withConverter(Order.converter);
    return getDocs(q)
      .then(snapshot => {
        if (snapshot.empty) {
          return null;
        }
        return snapshot.docs[0].data();
      })
      .catch(err => {
        console.error(err);
        throw err;
      });
  }

  /**
   * Get orders by transactionId
   * @param transactionId The order transaction id
   * @returns The orders or null
   */
  async searchOrderByTransactionId(transactionId: string): Promise<Order[]> {
    const q = query(collection(this.firestore, 'orders'),
      where('organizationId', '==', this.organizationService.currentOrganization.id),
      where('transactionId', '>=', transactionId),
      where('transactionId', '<=', transactionId + '\uf8ff'), limit(5))
      .withConverter(Order.converter);
    return getDocs(q)
      .then(snapshot => {
        if (snapshot.empty) {
          return [];
        }
        return snapshot.docs.map(doc => doc.data());
      })
      .catch(err => {
        console.error(err);
        throw err;
      });
  }

  /**
   * Search the orders whose reference starts with the given text
   * @param reference The start of the order reference
   * @returns The first matching orders
   */
  async searchOrderByReference(reference: string): Promise<Order[]> {
    const q = query(collection(this.firestore, 'orders'),
      where('organizationId', '==', this.organizationService.currentOrganization.id),
      where('reference', '>=', reference),
      where('reference', '<=', reference + '\uf8ff'), limit(5))
      .withConverter(Order.converter);
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => doc.data());
  }

  /**
   * Get an order by id
   * @param id The order id
   * @returns The order or null
   */
  public getOrderById(id: string): Promise<Order | null> {
    const ref = doc(this.firestore, 'orders', id).withConverter(Order.converter);
    return getDoc(ref)
      .then(snapshot => {
        if (!snapshot.exists()) {
          return null;
        }
        return snapshot.data();
      })
      .catch(err => {
        console.error(err);
        throw err;
      });
  }

  /**
   * Update order state
   * @param orderId The firebase id of the order
   * @param state The required state
   * @returns The promise
   */
  public updateOrderState(orderId: string, state: number): Promise<void> {
    const ref = doc(this.firestore, 'orders', orderId).withConverter(Order.converter);
    return updateDoc(ref, { state });
  }

  /**
   * Update an order
   * @param id The id of the order to update
   * @param partialOrder The fields of the order to update
   * @returns The promise
   */
  public update(id: string, partialOrder: Partial<Order>): Promise<void> {
    const ref = doc(this.firestore, 'orders', id).withConverter(Order.converter);
    return updateDoc(ref, ObjectHelper.anonymize(partialOrder));
  }

  /**
   * Get an access token
   * @param id The order transaction id
   * @param type The token type
   * @returns The query promise with the token url
   */
  public getAccessToken(id: string, type: number): Promise<string> {
    return httpsCallable<{
      id: string,
      type: number
    }, string>(this.functions, 'get_access_token_gen2')({ id, type })
      .then(result => result.data);
  }

  /**
   * Get order by id with token
   * @param id The order id
   * @param token The authorisation token
   * @returns A promise with the order
   */
  public getOrderWithToken(id: string, token: string): Promise<Order> {
    this.dataService.dataLoading = true;
    return httpsCallable<{
      id: string,
      token: string
    }, Order>(this.functions, 'get_order_by_token_gen2')({ id, token })
      .then(result => {
        const order: any = result.data;
        order.date = new Date(order.date._seconds * 1000);
        if (order.uploadsDate) {
          order.uploadsDate = order.uploadsDate ? new Date(order.uploadsDate._seconds * 1000) : null;
        }
        if (order.batDate) {
          order.batDate = order.batDate ? new Date(order.batDate._seconds * 1000) : null;
        }
        if (order.mailDate) {
          order.mailDate = order.mailDate ? new Date(order.mailDate._seconds * 1000) : null;
        }
        if (order.printDate) {
          order.printDate = order.printDate ? new Date(order.printDate._seconds * 1000) : null;
        }
        for (const qualityError of (order.qualityErrors ?? [])) {
          qualityError.date = qualityError.date ? new Date(qualityError.date._seconds * 1000) : null;
        }
        for (const product of (order.products ?? [])) {
          product.deliveryDate = product.deliveryDate ? new Date(product.deliveryDate._seconds * 1000) : null;
        }
        // The date the Multipress synchronization keeps up to date comes first: the one of the products is the one they
        // had at creation. The first product date only serves orders with no date of their own.
        const ownSeconds = order?.startDeliveryDate?._seconds ?? order?.startDeliveryDate?.seconds;
        order.startDeliveryDate = ownSeconds ? new Date(ownSeconds * 1000) :
          order?.products?.find(product => product.deliveryDate)?.deliveryDate ?? null;
        return new Order(order);
      })
      .catch(() => {
        return null;
      })
      .finally(() => this.dataService.dataLoading = false);
  }

  /**
   * Get order by id , checking customer right
   * @param id The order id
   * @param customerIds The list of customer ids allowed
   * @returns A promise with the order
   */
  public getOrderForCustomer(id: string, customerIds: string[]): Promise<Order | null> {
    const q = query(collection(this.firestore, 'orders'),
      where(documentId(), '==', id),
      where('organizationId', '==', this.organizationService.currentOrganization.id),
      where('customerId', 'in', customerIds))
      .withConverter(Order.converter);
    return getDocs(q)
      .then(snapshot => {
        if (snapshot.empty) {
          return null;
        }
        return snapshot.docs[0].data();
      })
      .catch(err => {
        console.error(err);
        throw err;
      });
  }

  /**
   * Send files required email
   * @param id The order id
   * @returns The promise
   */
  public sendFileEmail(id: string): Promise<void> {
    return httpsCallable<{
      orderId: string
    }, void>(this.functions, 'send_files_email_gen2')({ orderId: id })
      .then(result => result.data);
  }

  /**
   * Synchronize an order from multipress
   * @param organizationId The order transactionId
   * @param transactionId The order transactionId
   * @returns The promise
   */
  async syncOrder(organizationId: string, transactionId: string): Promise<void> {
    await httpsCallable<
      {
        organizationId: string,
        transactionId: string,
      },
      void>(this.functions, 'sync_order_gen2')({ organizationId, transactionId });
  }

  /**
   * Send required bat email
   * @param id The order id
   * @returns The promise
   */
  public sendBatEmail(id: string): Promise<void> {
    return httpsCallable<{
      orderId: string
    }, void>(this.functions, 'send_bat_email_gen2')({ orderId: id })
      .then(result => result.data);
  }

  /**
   * Force wait for validation state
   * @param id The order id
   * @returns The promise
   */
  public forceValidation(id: string): Promise<void> {
    return httpsCallable<{
      orderId: string
    }, void>(this.functions, 'force_validation_gen2')({ orderId: id })
      .then(result => result.data);
  }

  /**
   * Save proof validation
   * @param orderId The order id
   * @returns The promise
   */
  public updateBat(orderId: string): Promise<HttpsCallableResult<void>> {
    return httpsCallable<{ orderId: string }, void>(this.functions, 'update_bat_gen2')({ orderId });
  }

  /**
   * Update product data
   * @param order The order id
   * @returns The promise
   */
  async updateBatChoice(order: Order): Promise<void> {
    const ref = doc(this.firestore, 'orders', order.id)
      .withConverter(Order.converter);
    await updateDoc(ref, { products: ObjectHelper.anonymize(order.products) });
  }

  /**
   * Create files processing task
   * @param orderId The order
   * @returns The promise
   */
  public updateFiles(orderId: string): Promise<HttpsCallableResult<void>> {
    return httpsCallable<{
      orderId: string
    }, void>(this.functions, 'update_files_gen2')({ orderId });
  }

  /**
   * Get the files sync tasks
   * // TODO: subscribe to organization tasks
   * @returns The query observable
   */
  public getFilesTasks(): BehaviorSubject<FileTask[]> {
    if (!this.fileTasks$) {
      this.fileTasks$ = new BehaviorSubject<FileTask[]>([]);
      const q = query(collection(this.firestore, 'file-tasks'),
        where('inProgress', '==', true))
        .withConverter(FileTask.converter);
      this.unsubscribeFileTasks = onSnapshot(q, snapshot => {
        if (snapshot.empty) {
          return this.fileTasks$.next([]);
        }
        this.fileTasks$.next(snapshot.docs.map(doc => doc.data()));
      })
    }
    return this.fileTasks$;
  }

  /**
   * Get the import files tasks
   * // TODO: subscribe to organization tasks
   * @returns The query observable
   */
  public getImportTasks(): BehaviorSubject<FileImportTask[]> {
    if (!this.importTasks$) {
      this.importTasks$ = new BehaviorSubject<FileImportTask[]>([]);
      const q = query(collection(this.firestore, 'file-import-tasks'),
        where('inProgress', '==', true))
        .withConverter(FileImportTask.converter);
      this.unsubscribeImportTasks = onSnapshot(q, snapshot => {
        if (snapshot.empty) {
          return this.importTasks$.next([]);
        }
        this.importTasks$.next(snapshot.docs.map(doc => doc.data()));
      })
    }
    return this.importTasks$;
  }

  /**
   * Get active and failed nesting tasks (not yet processed)
   * @returns The nesting tasks behaviour subject
   */
  public getNestingTasks(): BehaviorSubject<NestingTask[]> {
    if (!this.nestingTasks$) {
      this.nestingTasks$ = new BehaviorSubject<NestingTask[]>([]);
      const organizationId = this.organizationService.currentOrganization?.id;
      const q = query(
        collection(this.firestore, 'nesting-tasks'),
        where('organizationId', '==', organizationId),
        where('processed', '==', false),
      ).withConverter(NestingTask.converter);
      this.unsubscribeNestingTasks = onSnapshot(q, snapshot => {
        this.nestingTasks$.next(snapshot.empty ? [] : snapshot.docs.map(doc => doc.data()));
      });
    }
    return this.nestingTasks$;
  }

  /**
   * Create a new orders synchronisation task
   * @param organizationId The organization id
   * @param orderId The order id
   * @param state The new order state
   * @param source Optional origin of the task, used to apply origin specific settings
   * @returns A reference to the created document
   */
  public async createUpdateStateTask(organizationId: string, orderId: string, state: OrderState,
    source?: 'PAO_CLOSE'): Promise<void> {
    const ref = collection(this.firestore, 'orders-states');
    await addDoc(ref, { date: new Date(), orderId, state, processed: false, organizationId,
      ...(source ? { source } : {}) });
  }

  /**
   * Delete okto local service generated files for this product
   * @param product The product
   * @returns The promise
   */
  public async cleanOldFiles(product: ProductOrder): Promise<void> {
    await this.uploadService.removeFile(product.poFiles[0].path.replace('.pdf', '-bat.pdf'));
    await this.uploadService.removeFile(product.poFiles[0].path.replace('.pdf', '-report-bat.pdf'));
  }

  public unsubscribe(): void {
    if (this.unsubscribeFileTasks) {
      this.unsubscribeFileTasks();
      this.fileTasks$ = null;
    }
    if (this.unsubscribeImportTasks) {
      this.unsubscribeImportTasks();
      this.importTasks$ = null;
    }
    if (this.unsubscribeNestingTasks) {
      this.unsubscribeNestingTasks();
      this.nestingTasks$ = null;
    }
  }

  /**
   * Retrieve an url of the zipped files
   * @param orderId The orderId
   * @param poId The poId
   * @param types The types of files to include in the zip
   * @param originalName Should we use the uploaded filename
   * @returns The Promise
   */
  public getOrderZip(orderId: string, poId: string, types: FileType[], originalName: boolean): Promise<string> {
    return httpsCallable<
      { orderId: string, poId?: string, types: FileType[], originalName: boolean },
      { url: string }>(this.functions, 'get_order_zip_gen2')(
        { orderId, poId, types, originalName })
      .then(res => res.data.url)
  }

  /**
   * Asynchronously downloads and retrieves a PDF as an ArrayBuffer for a specific organization and order id.
   * @param id - The unique identifier of the order.
   * @returns A promise that resolves to an ArrayBuffer containing the downloaded PDF file.
   */
  async download(id: string): Promise<string> {
    return httpsCallable<{ id: string }, string>
    (this.functions, 'get_production_pdf_gen2')({ id }).then(result => {
      return result.data;
    }).catch(err => {
      console.error(err);
      throw err;
    });
  }

  /**
   * Sets the purchase order number for a specific transaction within an organization.
   * @param orderId - The unique identifier of the order.
   * @param purchaseOrderNumber - The purchase order number to be set.
   * @param token - Unique token for the order.
   * @returns A promise that resolves to a string indicating the result of the operation.
   */
  async setPurchaseOrderNumber(orderId: string, purchaseOrderNumber: string, token: string)
    : Promise<void> {
    return httpsCallable<{ orderId: string, purchaseOrderNumber: string, token: string }, void>
    (this.functions, 'set_purchase_order_number_gen2')({
      orderId, purchaseOrderNumber, token
    }).then(result => {
      return result.data;
    }).catch(err => {
      console.error(err);
      throw err;
    });
  }
}
